/**
 * Parses a minutes offset out of an Intl 'shortOffset' timeZoneName value (e.g. "GMT+3",
 * "GMT-05:30"). Shared by {@link TimeZoneHelper} and {@link TimeZoneConstants} so both can
 * resolve the same 'shortOffset' format without duplicating the regex.
 *
 * @param offsetValue The 'shortOffset' timeZoneName string, e.g. "GMT+3".
 * @returns Parsed offset in minutes, or 0 if it cannot be parsed.
 */
export function parseShortOffsetMinutes(offsetValue: string): number {
  const match = /GMT([+-]\d{1,2})(?::?(\d{2}))?/.exec(offsetValue);

  if (!match) {
    return 0;
  }

  const sign = match[1].startsWith('-') ? -1 : 1;
  const hours = Math.abs(Number(match[1]));
  const minutes = match[2] ? Number(match[2]) : 0;

  return sign * (hours * 60 + minutes);
}

/**
 * Parses a legacy fixed-offset time zone value (exactly 5 characters: sign + HHMM, e.g.
 * "+0330", "-0330", "+1245") into a signed offset in minutes. A fixed offset is a constant UTC
 * offset with no DST. Anything else (IANA ids like "Europe/Berlin", "Etc/GMT-3", "UTC",
 * "+03:30", empty, null/undefined, non-string) is not a fixed offset.
 *
 * @param value The time zone value to inspect.
 * @returns Signed offset in minutes (e.g. "+0330" -> 210), or null if `value` is not a valid
 * fixed offset (minutes must be below 60 and hours at most 14).
 */
export function parseFixedOffsetTimeZone(value: string): number | null {
  if (typeof value !== 'string' || !/^[+-]\d{4}$/.test(value)) {
    return null;
  }

  const hours = Number(value.substring(1, 3));
  const minutes = Number(value.substring(3, 5));

  if (minutes >= 60 || hours > 14) {
    return null;
  }

  return (value[0] === '-' ? -1 : 1) * (hours * 60 + minutes);
}
